import { useNavigate } from 'react-router'
import { Button, Icon, Pill, Stack, Text } from '@/design-system'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { useAuth } from '@/app/auth/auth-context'
import { STEP_NUMBER, TOTAL_STEPS, useOnboardingDraft, useStep, type CardChoice } from './progress'
import styles from './Onboarding.module.css'

const CARD_ROW: Record<CardChoice, { title: string; body: string }> = {
  virtual: {
    title: 'Virtual card created',
    body: 'Activate it for $9 when you need it',
  },
  physical: {
    title: 'Physical card on its way',
    body: 'Nothing charged yet · $50 when you activate it on arrival',
  },
  both: {
    title: 'Virtual and physical cards created',
    body: '$9 to activate the virtual one · $50 for the plastic when it arrives',
  },
}

/**
 * 12 · All done — a receipt of what was set up. Registration charges nothing,
 * so accounts and cards are listed as waiting for activation, which is where
 * the first deposit and the card fees are paid.
 */
export function OnboardingReady() {
  useStep('ready')
  const navigate = useNavigate()
  const { completeOnboarding } = useAuth()
  const [draft] = useOnboardingDraft()

  const n = draft.accounts.length

  const items = [
    {
      icon: 'account_balance',
      title: `${n} ${n === 1 ? 'account' : 'accounts'} ready to activate`,
      body: `${draft.accounts.join(' · ')} · a first $50 deposit activates each`,
      done: false,
    },
    { icon: 'credit_card', ...CARD_ROW[draft.card], done: false },
    {
      icon: 'verified_user',
      title: 'Identity verified',
      body: 'No further documents needed',
      done: true,
    },
  ]

  const finish = (to: string) => {
    completeOnboarding()
    navigate(to, { replace: true })
  }

  return (
    <FlowLayout
      step={STEP_NUMBER.ready}
      totalSteps={TOTAL_STEPS}
      footer={
        <>
          <Button size="lg" block iconEnd="arrow_forward" onClick={() => finish('/')}>
            Go to my dashboard
          </Button>
          {draft.card !== 'physical' && (
            <Button
              variant="ghost"
              size="md"
              block
              onClick={() => finish('/open/card?card=virtual')}
            >
              Activate my virtual card · $9
            </Button>
          )}
          <Button variant="ghost" size="md" block onClick={() => finish('/accounts')}>
            Activate my accounts
          </Button>
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text variant="eyebrow" weight="semibold" tone="secondary">
            All done
          </Text>
          <Text as="h1" variant="title" weight="bold">
            You are all set{draft.name ? `, ${draft.name.split(' ')[0]}` : ''}
          </Text>
          <Text variant="body" tone="secondary">
            Nothing was charged. Activate your accounts and your card whenever you are ready.
          </Text>
        </Stack>

        <Stack gap={2}>
          {items.map((item) => (
            <div
              key={item.title}
              className={item.done ? `${styles.row} ${styles.rowDone}` : styles.row}
            >
              <Icon name={item.icon} size={20} />
              <Stack grow gap={0}>
                <Text variant="caption" weight="semibold">
                  {item.title}
                </Text>
                <Text variant="caption" tone="secondary">
                  {item.body}
                </Text>
              </Stack>
              {item.done ? (
                <Icon name="check_circle" size={20} filled />
              ) : (
                <Pill tone="warning">To activate</Pill>
              )}
            </div>
          ))}
        </Stack>
      </Stack>
    </FlowLayout>
  )
}
